import { call, put, takeEvery, all } from "redux-saga/effects";
import { db } from "helpers/firebase/firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
  getDoc,
  addDoc,
  setDoc,
  increment,
  where,
  limit,
} from "firebase/firestore";
import axios from "axios";
import * as actionTypes from "./actionTypes";
import {
  fetchRefundsSuccess,
  fetchRefundsFailure,
  approveRefundSuccess,
  approveRefundFailure,
  rejectRefundSuccess,
  rejectRefundFailure,
  markRefundRefundedSuccess,
  markRefundRefundedFailure,
  generateRefundFromFlaggedInvoiceSuccess,
  generateRefundFromFlaggedInvoiceFailure,
} from "./actions";

const REFUND_COLLECTION = "refund_requests";
const APP_NOTIFICATIONS_COLLECTION = "app_notifications";
const PROVIDER_NOTIFICATIONS_COLLECTION = "serviceProvidersNotifications";
const COMMISSION_INVOICES_COLLECTION = "service_provider_sale_invoices";
const CUSTOMER_SETTLEMENTS_COLLECTION = "customer_settlements";
const CREDIT_NOTES_COLLECTION = "credit_notes";
const PLATFORM_REVENUE_COLLECTION = "platform_revenue";
const NODE_API_BASE = "https://node-server-eta-coral.vercel.app";
const NODE_API_KEY = process.env.REACT_APP_NODE_API_KEY;

const buildApiHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  if (NODE_API_KEY) headers["x-api-key"] = NODE_API_KEY;
  return headers;
};

const parseServerMessages = (msgStr) => {
  if (!msgStr) return "";
  try {
    const arr = JSON.parse(msgStr);
    return arr
      .map((m) => {
        try {
          const parsed = JSON.parse(m);
          return parsed?.message || m;
        } catch {
          return m;
        }
      })
      .join(" | ");
  } catch {
    return msgStr;
  }
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const roundMoney = (value) => Math.round(toNumber(value) * 100) / 100;

const resolveRefundBaseAmount = (source) => {
  return (
    toNumber(source?.gross_amount) ||
    toNumber(source?.original_service_amount) ||
    toNumber(source?.total_after_discount_before_fine) ||
    toNumber(source?.total_after_discount) ||
    toNumber(source?.gross_sale_amount) ||
    toNumber(source?.amount_paid) ||
    toNumber(source?.invoice_amount) ||
    toNumber(source?.amount) ||
    toNumber(source?.total_platform_revenue)
  );
};

const deriveRefundAmounts = (source) => {
  const rawFineDeduction =
    toNumber(source?.fine_deduction_amount) ||
    toNumber(source?.fine_deductions) ||
    toNumber(source?.fine_deductions_total);

  const cancelledBy = String(source?.cancelled_by || "").toLowerCase();
  const fineChargedTo = String(
    source?.fine_charged_to || (cancelledBy === "app_user" || cancelledBy === "app__user" ? "app_user" : "provider")
  ).toLowerCase();
  const fineDeduction = fineChargedTo === "app_user" ? rawFineDeduction : 0;

  const grossWithFine = resolveRefundBaseAmount(source);

  const commissionsWithVatNet =
    toNumber(source?.app_commission) +
    toNumber(source?.app_commission_vat) +
    toNumber(source?.service_provider_commission) +
    toNumber(source?.service_provider_commission_vat);

  const grossCommission = grossWithFine || (commissionsWithVatNet + fineDeduction);

  const netRefund =
    grossWithFine > 0
      ? Math.max(0, grossWithFine - fineDeduction)
      : Math.max(0, commissionsWithVatNet);

  return {
    grossCommission: roundMoney(grossCommission),
    fineDeduction: roundMoney(fineDeduction),
    fineChargedTo,
    netRefund: roundMoney(netRefund),
  };
};

function* createCreditNoteFromInvoice(refundData) {
  if (!refundData?.erp_invoice_id) {
    throw new Error("Missing ERP invoice ID on refund request");
  }

  const invoice = yield call(getInvoiceData, refundData.erp_invoice_id);
  // console.log("Fetched invoice data for credit note creation:", invoice);
  if (!invoice?.name) {
    throw new Error("Original ERP invoice not found for refund");
  }

  const today = new Date().toISOString().split("T")[0];
  const derived = deriveRefundAmounts(refundData);
  const grandTotal = roundMoney(Math.abs(
    toNumber(refundData.net_refund_amount ?? refundData.invoice_amount ?? refundData.amount) ||
      derived.netRefund ||
      toNumber(invoice.grand_total)
  ));

  const itemCode =
    refundData.commission_item_code ||
    invoice?.items?.[0]?.item_code ||
    "DB-COMMISSION-001";

  const itemsPayload = [
    {
      item_code: itemCode,
      qty: -1,
      rate: grandTotal,
      income_account: invoice.income_account,
      item_tax_template: invoice.item_tax_template,
    },
  ];

  const paymentsPayload =
    Array.isArray(invoice.payments) && invoice.payments.length > 0
      ? invoice.payments.map((p) => ({
          mode_of_payment: p.mode_of_payment || "Cash",
          account: p.account || invoice.debit_to,
          amount: -Math.abs(Number(p.amount ?? grandTotal)),
          base_amount: -Math.abs(Number(p.base_amount ?? grandTotal)),
        }))
      : [
          {
            mode_of_payment: "Cash",
            account: invoice.debit_to,
            amount: -grandTotal,
            base_amount: -grandTotal,
          },
        ];

  const payload = {
    customer: invoice.customer || invoice.customer_name,
    is_return: 1,
    docstatus: 1,
    company: invoice.company,
    return_against: invoice.name,
    update_outstanding_for_self: 0,
    is_pos: invoice.is_pos ? 1 : 0,
    cost_center: invoice.cost_center,
    posting_date: today,
    due_date: today,
    debit_to: invoice.debit_to,
    paid_amount: grandTotal,
    items: itemsPayload,
    payments: paymentsPayload,
  };
  // console.log("Creating credit note with payload:", payload);
  const res = yield call(fetch, `${NODE_API_BASE}/refund-credit-note`, {
    method: "POST",
    headers: buildApiHeaders(),
    body: JSON.stringify(payload),
  });

  const json = yield call([res, "json"]);
  const serverMessage = parseServerMessages(json?._server_messages);
  if (!res.ok || json?.error || json?.exc_type) {
    throw new Error(
      serverMessage || json?.message || json?.error || "Failed to create credit note"
    );
  }

  const creditNoteData = json?.data || json;
// console.log("Credit note created:", creditNoteData);
  yield call(addDoc, collection(db, CREDIT_NOTES_COLLECTION), {
    refund_id: refundData.id,
    erp_invoice_id: invoice.name,
    credit_note_id: creditNoteData?.name,
    credit_note_data: creditNoteData,
    customer_name: creditNoteData?.customer_name || invoice.customer_name,
    amount: grandTotal,
    status: "completed",
    created_at: new Date().toISOString(),
    created_by: "system",
  });

  return creditNoteData;
}

function* updateInvoiceAndSettlementAfterRefund(erpInvoiceId, creditNoteData) {
  if (!erpInvoiceId) return null;

  const invoiceQuery = query(
    collection(db, COMMISSION_INVOICES_COLLECTION),
    where("commission_erp_id", "==", erpInvoiceId),
    limit(1)
  );

  const invoiceSnap = yield call(getDocs, invoiceQuery);
  if (invoiceSnap.empty) return null;

  const invDoc = invoiceSnap.docs[0];
  const invoiceData = invDoc.data();

  const commissionRevenue = deriveRefundAmounts(invoiceData).netRefund;

  const invoiceUpdates = {
    refund_status: "refunded",
    refund_erp_id: creditNoteData?.name || null,
    refunded_at: new Date().toISOString(),
    original_invoice: erpInvoiceId,
  };

  yield call(updateDoc, invDoc.ref, invoiceUpdates);

  if (invoiceData?.settlement_id) {
    yield call(
      updateDoc,
      doc(db, CUSTOMER_SETTLEMENTS_COLLECTION, invoiceData.settlement_id),
      {
        status: "reversed",
        reversed_at: new Date().toISOString(),
        reversed_by: "system",
      }
    );
  }

  if (commissionRevenue !== 0) {
    yield call(
      setDoc,
      doc(db, PLATFORM_REVENUE_COLLECTION, "total"),
      {
        amount: increment(-commissionRevenue),
        last_updated: new Date().toISOString(),
        last_refund_erp_id: creditNoteData?.name || null,
      },
      { merge: true }
    );
  }

  return { invoiceId: invDoc.id, commissionRevenue };
}

function* getInvoiceData(invoiceId) {
  if (!invoiceId) return null;
  const resp = yield call(
    fetch,
        `https://node-server-eta-coral.vercel.app/invoice-details/${encodeURIComponent(invoiceId)}`,
    { headers: { 
      "Content-Type": "application/json",
       'x-api-key': process.env.REACT_APP_NODE_API_KEY 
      } }
  );
  const json = yield call([resp, "json"]);
  return json?.data?.[0] || null;
}

function* callTamaraSimplifiedRefund(tamaraOrderId, refundAmount, currency, merchantRefundId) {
  const res = yield call(
    fetch,
    `${NODE_API_BASE}/tamara/simplified-refund/${encodeURIComponent(tamaraOrderId)}`,
    {
      method: "POST",
      headers: buildApiHeaders(),
      body: JSON.stringify({
        total_amount: {
          amount: refundAmount,
          currency: currency || "SAR",
        },
        comment: `Refund for order ${tamaraOrderId}`,
        merchant_refund_id: merchantRefundId || undefined,
      }),
    }
  );
  const json = yield call([res, "json"]);
  if (!res.ok || json?.success === false) {
    throw new Error(
      json?.message || json?.error_code || "Tamara simplified refund failed"
    );
  }
  return json;
}

function* fetchRefunds() {
  try {
    const refundsRef = collection(db, REFUND_COLLECTION);
    const snapshot = yield call(getDocs, refundsRef);

    const refunds = snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      if (data.requested_date?.seconds) {
        data.requested_date = new Date(data.requested_date.seconds * 1000).toISOString();
      }
      return { id: docSnap.id, ...data };
    });

    const enrichedRefunds = yield all(
      refunds.map((r) =>
        call(function* () {
          try {
            const invoice = yield call(getInvoiceData, r.erp_invoice_id);
            const derived = deriveRefundAmounts(r);
            return {
              ...r,
              customer_name: r.customer_name || invoice?.customer_name,
              invoice_amount:
                toNumber(r.net_refund_amount ?? r.invoice_amount ?? r.amount) ||
                derived.netRefund,
              original_invoice_amount:
                toNumber(r.gross_amount) || toNumber(invoice?.grand_total),
              ksa_einv_qr: invoice?.ksa_einv_qr || r.ksa_einv_qr,
            };
          } catch {
            return r;
          }
        })
      )
    );

    yield put(fetchRefundsSuccess(enrichedRefunds));
  } catch (error) {
    yield put(fetchRefundsFailure(error.message));
  }
}

function* approveRefund(action) {
  try {
    const { refundId, creditNoteData } = action.payload;

    const refundDocRef = doc(db, REFUND_COLLECTION, refundId);
    const refundSnapshot = yield call(getDoc, refundDocRef);
    const refundData = refundSnapshot.exists()
      ? { id: refundSnapshot.id, ...refundSnapshot.data() }
      : { id: refundId };

    let creditNoteResult = creditNoteData;
    if (!creditNoteResult?.name) {
      creditNoteResult = yield call(createCreditNoteFromInvoice, refundData);
    }

    // ── Tamara Payment: call Simplified Refund API ──────────────────────────
    let tamaraRefundResult = null;
    const paymentMethod = String(
      refundData.payment_method ||
      refundData.booking?.paymentMethod ||
      refundData.booking?.payment_method ||
      ""
    ).toLowerCase();

    if (paymentMethod === "tamara") {
      const tamaraOrderId = refundData.booking_uuid || refundData.tamara_order_id;
      if (tamaraOrderId) {
        try {
          const refundAmount = toNumber(
            refundData.net_refund_amount ?? refundData.invoice_amount ?? refundData.amount
          );
          tamaraRefundResult = yield call(
            callTamaraSimplifiedRefund,
            tamaraOrderId,
            refundAmount,
            refundData.currency || "SAR",
            refundData.id
          );
        } catch (tamaraErr) {
          console.error("Tamara refund API error:", tamaraErr.message);
          // Non-fatal: log but continue so the internal refund still completes
          tamaraRefundResult = { error: tamaraErr.message };
        }
      }
    }
    // ────────────────────────────────────────────────────────────────────────

    const nowIso = new Date().toISOString();

    const updateData = {
      status: "completed",
      refunded: true,
      approved_at: nowIso,
      refunded_at: nowIso,
      refund_status: "refunded",
      original_invoice: refundData.erp_invoice_id,
    };

    if (creditNoteResult?.name) {
      updateData.credit_note_id = creditNoteResult.name;
      updateData.credit_note_data = creditNoteResult;
    }

    if (tamaraRefundResult && !tamaraRefundResult.error) {
      updateData.tamara_refund_id = tamaraRefundResult.refund_id || null;
      updateData.tamara_capture_id = tamaraRefundResult.capture_id || null;
      updateData.tamara_refund_status = tamaraRefundResult.status || null;
    } else if (tamaraRefundResult?.error) {
      updateData.tamara_refund_error = tamaraRefundResult.error;
    }

    yield call(updateDoc, refundDocRef, updateData);

    yield call(
      updateInvoiceAndSettlementAfterRefund,
      refundData.erp_invoice_id,
      creditNoteResult
    );

    const updatedSnapshot = yield call(() => getDocs(collection(db, REFUND_COLLECTION)));
    const updatedRefund = updatedSnapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .find((r) => r.id === refundId);

    try {
      const transactionSnapshot = yield call(() =>
        getDocs(collection(db, "providers_transactions_history"))
      );

      const transaction = transactionSnapshot.docs.find((t) => {
        const data = t.data();
        return data.erp_invoice_id === updatedRefund.erp_invoice_id;
      });

      if (!transaction) {
        yield put(approveRefundSuccess(updatedRefund));
        return;
      }

      const transactionData = transaction.data();

      const [userSnapshot, providerSnapshot] = yield all([
        call(getDoc, doc(db, "app_users", transactionData.app_user_id || "")),
        call(getDoc, doc(db, "service_providers", transactionData.service_providers_id || "")),
      ]);

      const userData = userSnapshot.exists() ? userSnapshot.data() : null;
      const providerData = providerSnapshot.exists() ? providerSnapshot.data() : null;

      if (userData?.notification_token) {
        const appNotifData = {
          title: "تم الموافقة على طلب الاسترجاع",
          content: "تم الموافقة على طلب الاسترجاع الخاص بك. سيتم استرجاع المبلغ قريباً.",
          notification_type: "REFUND",
          refundId,
          isSeen: false,
          appUser: transactionData.app_user_id,
          created_at: new Date().toISOString(),
        };
        yield call(sendAppNotification, appNotifData, userData.notification_token);
      }

      if (providerData?.notification_token) {
        const providerTokens = Array.isArray(providerData.notification_token)
          ? providerData.notification_token
          : [providerData.notification_token];

        for (const token of providerTokens) {
          const providerNotifData = {
            title: "تم الموافقة على طلب الاسترجاع",
            content: "تم الموافقة على طلب الاسترجاع للعميل. سيتم معالجة الاسترجاع.",
            notification_type: "REFUND",
            refundId,
            seen: false,
            serviceProviders: transactionData.service_providers_id,
            created_at: new Date().toISOString(),
          };
          yield call(sendProviderNotification, providerNotifData, token);
        }
      }
    } catch (notifError) {
      console.error("Notification error:", notifError);
    }

    yield put(approveRefundSuccess(updatedRefund));
  } catch (error) {
    console.error("Approve refund error:", error);
    yield put(approveRefundFailure(error.message));
  }
}

function* markRefundRefunded(action) {
  try {
    const { refundId } = action.payload;
    // console.log("Marking refund as refunded:", refundId);

    const refundDocRef = doc(db, REFUND_COLLECTION, refundId);

    yield call(updateDoc, refundDocRef, {
      refunded: true,
      status: "completed",
      refunded_at: new Date().toISOString(),
    });

    const updatedSnapshot = yield call(() => getDocs(collection(db, REFUND_COLLECTION)));
    const updatedRefund = updatedSnapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .find((r) => r.id === refundId);

    // console.log("Updated refund:", updatedRefund);

    try {
      const transactionSnapshot = yield call(() =>
        getDocs(collection(db, "providers_transactions_history"))
      );

      const transaction = transactionSnapshot.docs.find((t) => {
        const data = t.data();
        return data.erp_invoice_id === updatedRefund.erp_invoice_id;
      });

      if (!transaction) {
        // console.log("No matching transaction found");
        yield put(markRefundRefundedSuccess(updatedRefund));
        return;
      }

      const transactionData = transaction.data();
      // console.log("Transaction for mark refund:", transactionData);

      const [userSnapshot, providerSnapshot] = yield all([
        call(getDoc, doc(db, "app_users", transactionData.app_user_id || "")),
        call(getDoc, doc(db, "service_providers", transactionData.service_providers_id || "")),
      ]);

      const userData = userSnapshot.exists() ? userSnapshot.data() : null;
      const providerData = providerSnapshot.exists() ? providerSnapshot.data() : null;

      if (userData?.notification_token) {
        const appNotifData = {
          title: "تم استرجاع المبلغ",
          content: "تم استرجاع المبلغ بنجاح إلى حسابك.",
          notification_type: "REFUND_COMPLETED",
          refundId,
          isSeen: false,
          appUser: transactionData.app_user_id,
          created_at: new Date().toISOString(),
        };
        yield call(sendAppNotification, appNotifData, userData.notification_token);
      }

      if (providerData?.notification_token) {
        const providerTokens = Array.isArray(providerData.notification_token)
          ? providerData.notification_token
          : [providerData.notification_token];

        for (const token of providerTokens) {
          const providerNotifData = {
            title: "تم معالجة الاسترجاع",
            content: "تم معالجة طلب الاسترجاع بنجاح.",
            notification_type: "REFUND_COMPLETED",
            refundId,
            seen: false,
            serviceProviders: transactionData.service_providers_id,
            created_at: new Date().toISOString(),
          };
          yield call(sendProviderNotification, providerNotifData, token);
        }
      }
    } catch (notifError) {
      console.error("Notification error:", notifError);
    }

    yield put(markRefundRefundedSuccess(updatedRefund));
  } catch (error) {
    console.error("Mark refund error:", error);
    yield put(markRefundRefundedFailure(error.message));
  }
}

function* sendAppNotification(notificationData, deviceToken) {
  const API_URL = "https://node-server-eta-coral.vercel.app/send-notification";

  try {
    if (deviceToken) {
      yield call(
        axios.post,
        API_URL,
        {
          deviceToken,
          title: notificationData.title,
          body: notificationData.content,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_NODE_API_KEY,
          },
        }
      );
    }

    yield call(addDoc, collection(db, APP_NOTIFICATIONS_COLLECTION), notificationData);
  } catch (error) {
    console.error("App notification error:", error);
  }
}

function* sendProviderNotification(notificationData, deviceToken) {
  const API_URL = "https://node-server-eta-coral.vercel.app/send-notification";

  try {
    if (deviceToken) {
      yield call(
        axios.post,
        API_URL,
        {
          deviceToken,
          title: notificationData.title,
          body: notificationData.content,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_NODE_API_KEY,
          },
        }
      );
    }

    yield call(addDoc, collection(db, PROVIDER_NOTIFICATIONS_COLLECTION), notificationData);
  } catch (error) {
    console.error("Provider notification error:", error);
  }
}

function* rejectRefund(action) {
  try {
    const { refundId, reason } = action.payload;

    const refundDocRef = doc(db, REFUND_COLLECTION, refundId);
    yield call(updateDoc, refundDocRef, {
      status: "rejected",
      rejected_at: new Date().toISOString(),
      rejection_reason: reason,
    });

    const updatedSnapshot = yield call(() => getDocs(collection(db, REFUND_COLLECTION)));
    const updatedRefund = updatedSnapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .find((r) => r.id === refundId);

    yield put(rejectRefundSuccess(updatedRefund));
  } catch (error) {
    yield put(rejectRefundFailure(error.message));
  }
}

function* generateRefundFromFlaggedInvoice(action) {
  try {
    const { invoiceData } = action.payload;
    
    // console.log("📋 Refund generation request received. Invoice data:", invoiceData);
    
    if (!invoiceData?.id) {
      console.error("❌ Invoice ID missing. Available fields:", Object.keys(invoiceData || {}));
      throw new Error(`Invoice ID is required. Available fields: ${Object.keys(invoiceData || {}).join(', ')}`);
    }

    // console.log("📋 Generating refund from flagged invoice:", invoiceData.id);

    // ✅ STEP 1: Fetch full invoice data from Firestore
    const invoiceRef = doc(db, COMMISSION_INVOICES_COLLECTION, invoiceData.id);
    const fullInvoiceSnap = yield call(getDoc, invoiceRef);
    
    if (!fullInvoiceSnap.exists()) {
      throw new Error("Invoice not found in Firestore");
    }

    const fullInvoiceData = { id: fullInvoiceSnap.id, ...fullInvoiceSnap.data() };
    // console.log("📄 Full invoice data fetched:", fullInvoiceData);

    // ✅ STEP 2: Check if ERP commission invoice exists, if not create it
    let erpInvoiceId = fullInvoiceData.commission_erp_id;

    if (!erpInvoiceId) {
      // console.log("⚙️ ERP commission invoice not found - creating it now...");
      
      // Verify we have commission breakdown
      if (!fullInvoiceData.app_commission && !fullInvoiceData.service_provider_commission) {
        throw new Error("Invoice missing commission breakdown data - cannot create ERP invoice or generate refund");
      }

      // Create ERP commission invoice using dbInvoices action
      // We'll dispatch the confirmPayment action which creates the ERP invoice
      // console.log("🔄 Calling payment confirmation to create ERP invoice...");
      
      // Import and call the updateStatus function from dbInvoices saga
      // For now, we'll throw a helpful error directing admin to finalize payment first
      throw new Error(
        "الفاتورة لم يتم إنشاؤها في نظام ERP بعد. " +
        "يرجى الذهاب إلى صفحة 'دفع المستحق' والضغط على 'إنشاء فاتورة' أولاً، " +
        "ثم العودة هنا لإنشاء طلب الاسترجاع."
      );
    }



    // ✅ STEP 3: Create refund_request record
    const derived = deriveRefundAmounts(fullInvoiceData);
    const refundAmount = roundMoney(derived.netRefund);
    const refundGrossAmount = roundMoney(resolveRefundBaseAmount(fullInvoiceData));

    const refundPayload = {
      invoice_id: fullInvoiceData.id,
      erp_invoice_id: erpInvoiceId,
      commission_erp_id: erpInvoiceId,
      
      // Refund amount after fine deduction
      amount: refundAmount,
      invoice_amount: refundAmount,
      net_refund_amount: refundAmount,
      gross_amount: refundGrossAmount,
      fine_deduction_amount: roundMoney(derived.fineDeduction),
      fine_charged_to: derived.fineChargedTo,
      
      // Commission breakdown for tracking
      app_commission: fullInvoiceData.app_commission || 0,
      app_commission_vat: fullInvoiceData.app_commission_vat || 0,
      service_provider_commission: fullInvoiceData.service_provider_commission || 0,
      service_provider_commission_vat: fullInvoiceData.service_provider_commission_vat || 0,
      fine_deductions: roundMoney(derived.fineDeduction),
      
      // Refund reason from provider cancellation
      reason: fullInvoiceData.refund_reason || "Provider cancelled order",
      refund_type: "commission_only",
      
      // Reference to the cancellation
      cancelled_order_id: fullInvoiceData.cancelled_order_id || null,
      refund_initiated_at: fullInvoiceData.refund_initiated_at || new Date().toISOString(),
      booking_uuid: fullInvoiceData.booking_uuid || null,
      // Status
      status: "pending",
      requested_date: new Date().toISOString(),
      source: "admin_refund_generation_from_flagged_invoice",
      
      // Customer and provider info
    
      customer_name: invoiceData.app_user_id.username || "Unknown",
      service_provider_id: fullInvoiceData.serviceProviders || null,
      service_provider_name: invoiceData.serviceProviders.full_name || null,
      service_name: invoiceData.serviceProviderServices.name || null,
      commission_item_code: fullInvoiceData.commission_item_code || null,

    };

    const refundDocRef = yield call(
      addDoc,
      collection(db, REFUND_COLLECTION),
      refundPayload
    );

    // console.log("✅ Refund request created:", refundDocRef);

    // ✅ STEP 4: Update invoice to mark refund as generated
    yield call(updateDoc, doc(db, COMMISSION_INVOICES_COLLECTION, fullInvoiceData.id), {
      needs_refund: false,
      refund_status: "pending",
      refund_generated_at: new Date().toISOString(),
      refund_request_id: refundDocRef.id,
    });

    // ✅ STEP 5: Fetch updated refund and return
    const updatedRefundSnap = yield call(getDoc, refundDocRef);
    const generatedRefund = {
      id: updatedRefundSnap.id,
      ...updatedRefundSnap.data(),
    };

    // console.log("✅ Refund generated successfully from flagged invoice");

    yield put(generateRefundFromFlaggedInvoiceSuccess(generatedRefund));
  } catch (error) {
    console.error("❌ Error generating refund from flagged invoice:", error.message);
    yield put(generateRefundFromFlaggedInvoiceFailure(error.message));
  }
}

function* fetchRefundsSaga() {
  yield call(fetchRefunds);
}

function* refundSaga() {
  yield takeEvery(actionTypes.FETCH_REFUNDS_REQUEST, fetchRefundsSaga);
  yield takeEvery(actionTypes.APPROVE_REFUND_REQUEST, approveRefund);
  yield takeEvery(actionTypes.REJECT_REFUND_REQUEST, rejectRefund);
  yield takeEvery(actionTypes.MARK_REFUND_REFUNDED_REQUEST, markRefundRefunded);
  yield takeEvery(actionTypes.GENERATE_REFUND_FROM_FLAGGED_INVOICE_REQUEST, generateRefundFromFlaggedInvoice);
}

export default refundSaga;
